import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/features/auth/useAuth'
import type { CallRow, ProfileRow } from '@/types/database'
import { declineCall } from './start-call'
import { Avatar, Button } from '@/components/ui'
import { useT } from '@/i18n/useT'

/**
 * Rings while the app is open. Mounted once, above the router.
 *
 * When the app is closed this cannot fire at all -- that path is Web Push
 * (see src/workers/sw.ts). On iOS Safari there is no VoIP push, so an
 * incoming call to a fully-closed iOS PWA will not ring. That limitation is
 * the main reason to ship a Capacitor shell with CallKit later.
 */
export function IncomingCallListener() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const t = useT()
  const [incoming, setIncoming] = useState<{ call: CallRow; caller: ProfileRow } | null>(null)
  const ringtone = useRef<HTMLAudioElement | null>(null)

  useEffect(() => {
    if (!user) return

    const channel = supabase
      .channel('incoming-calls')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'calls' },
        async ({ new: row }) => {
          const call = row as CallRow
          // RLS already limits this to conversations we belong to; skip our
          // own outgoing call.
          if (call.initiator_id === user.id || call.status !== 'ringing') return

          const { data: caller } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', call.initiator_id)
            .single()
          if (caller) setIncoming({ call, caller })
        },
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'calls' },
        ({ new: row }) => {
          const call = row as CallRow
          // Caller hung up, or this person answered on another device. Both
          // mean this client should stop ringing -- one person with a browser
          // tab and the installed app open would otherwise have two clients
          // trying to be the far end of the same call.
          if (call.status !== 'ringing') {
            setIncoming((prev) => (prev?.call.id === call.id ? null : prev))
          }
        },
      )
      .subscribe()

    return () => {
      void supabase.removeChannel(channel)
    }
  }, [user])

  // Ringtone + vibration while the banner is up.
  useEffect(() => {
    if (!incoming) {
      ringtone.current?.pause()
      return
    }
    const audio = new Audio('/sounds/ring.mp3')
    audio.loop = true
    ringtone.current = audio
    // Autoplay may be blocked until the user has interacted with the page;
    // the vibration and the visible banner still get their attention.
    void audio.play().catch(() => {})

    const buzz = window.setInterval(() => navigator.vibrate?.([400, 200, 400]), 1500)

    return () => {
      audio.pause()
      window.clearInterval(buzz)
      window.clearInterval(buzz)
      navigator.vibrate?.(0)
    }
  }, [incoming])

  if (!incoming) return null

  const { call, caller } = incoming

  return (
    <div
      role="alertdialog"
      aria-label={`${call.kind === 'video' ? t('call.incomingVideo') : t('call.incomingVoice')} — ${caller.display_name}`}
      className="surface-raised safe-top border-hair fixed inset-x-3 top-3 z-[100] flex items-center gap-3 rounded-2xl border p-3 shadow-2xl"
    >
      <Avatar id={caller.id} name={caller.display_name} src={caller.avatar_url} size={48} />
      <div className="min-w-0 flex-1">
        <p className="truncate font-semibold">{caller.display_name}</p>
        <p className="text-muted text-xs">
          {call.kind === 'video' ? t('call.incomingVideo') : t('call.incomingVoice')}
        </p>
      </div>
      <Button
        variant="danger"
        size="sm"
        onClick={() => {
          void declineCall(call.id)
          setIncoming(null)
        }}
      >
        {t('call.decline')}
      </Button>
      <Button
        size="sm"
        className="bg-green-600 hover:bg-green-700"
        onClick={() => {
          setIncoming(null)
          navigate(`/call/${call.id}`)
        }}
      >
        {t('call.accept')}
      </Button>
    </div>
  )
}
